import { Pressable, Text, View } from 'react-native'
import { Minus, Plus } from 'lucide-react-native'
import { colors } from '@chillout/core'

interface StepperProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max: number
  label?: string
  countLabel: string
  helperText?: string
  disabled?: boolean
  decrementAccessibilityLabel: string
  incrementAccessibilityLabel: string
  countAccessibilityLabel?: string
}

export function Stepper({
  value,
  onChange,
  min = 1,
  max,
  label,
  countLabel,
  helperText,
  disabled = false,
  decrementAccessibilityLabel,
  incrementAccessibilityLabel,
  countAccessibilityLabel,
}: StepperProps) {
  const isMinDisabled = disabled || value <= min
  const isMaxDisabled = disabled || value >= max

  return (
    <View className="gap-2">
      {label ? (
        <Text className="text-sm font-semibold text-primary">{label}</Text>
      ) : null}

      <View className="flex-row items-center overflow-hidden rounded-lg border border-border">
        <Pressable
          onPress={() => onChange(Math.max(min, value - 1))}
          disabled={isMinDisabled}
          accessibilityRole="button"
          accessibilityLabel={decrementAccessibilityLabel}
          accessibilityState={{ disabled: isMinDisabled }}
          className={`h-14 w-16 web:lg:h-auto web:lg:py-2.5 items-center justify-center ${isMinDisabled ? 'bg-canvas-primary' : 'bg-accent-surface active:opacity-70'}`}
        >
          <Minus
            size={24}
            color={isMinDisabled ? colors.text.muted : colors.accent.DEFAULT}
            strokeWidth={2.5}
          />
        </Pressable>

        <View className="w-px self-stretch bg-border" />

        <View
          className="flex-1 items-center justify-center"
          accessibilityLabel={countAccessibilityLabel}
        >
          <Text className="text-base font-semibold text-primary">{countLabel}</Text>
        </View>

        <View className="w-px self-stretch bg-border" />

        <Pressable
          onPress={() => onChange(Math.min(max, value + 1))}
          disabled={isMaxDisabled}
          accessibilityRole="button"
          accessibilityLabel={incrementAccessibilityLabel}
          accessibilityState={{ disabled: isMaxDisabled }}
          className={`h-14 w-16 web:lg:h-auto web:lg:py-2.5 items-center justify-center ${isMaxDisabled ? 'bg-canvas-primary' : 'bg-accent-surface active:opacity-70'}`}
        >
          <Plus
            size={24}
            color={isMaxDisabled ? colors.text.muted : colors.accent.DEFAULT}
            strokeWidth={2}
          />
        </Pressable>
      </View>

      {helperText ? (
        <Text className="text-sm font-normal text-secondary">{helperText}</Text>
      ) : null}
    </View>
  )
}